// src/services/shippingApi.js
import { addDoc, collection, doc, getDoc, serverTimestamp, updateDoc, } from "firebase/firestore";
import { db } from "../services/firebase";

/**
 * Construit un "draft" à partir d'une liste de PO ids:
 * - charge les PO
 * - pour chaque ligne, calcule le restant (qtyOrdered - qtyReceived)
 * - retourne un tableau "lines" à éditer côté UI
 */
export async function buildShipmentDraftFromPOs(poIds) {
  if (!poIds?.length) throw new Error("Aucun PO sélectionné.");
  const pos = [];
  for (const id of poIds) {
    const snap = await getDoc(doc(db, "purchaseOrders", id));
    if (snap.exists()) pos.push({ id, ...snap.data() });
  }
  const draftLines = [];
  pos.forEach(po => {
    const items = Array.isArray(po.items) ? po.items : [];
    items.forEach(it => {
      const ordered = Number(it.qtyOrdered || 0);
      const received = Number(it.qtyReceived || 0);
      const remain = Math.max(0, ordered - received);
      if (remain > 0) {
        draftLines.push({
          poId: po.id,
          itemId: it.itemId,
          name: it.name || null,
          remain,
          unitCost: Number(it.unitCost || 0),
          qtyPlanned: remain,   // par défaut on propose tout le restant, l'UI peut réduire
        });
      }
    });
  });
  return { pos, lines: draftLines };
}

/**
 * Crée un shipment (enregistrement "plan") sans modifier les PO:
 * - header: { poIds[], status:"draft"|"in_transit", meta... }
 * - lines : [{poId,itemId,qtyPlanned,unitCost,name}]
 * - Retourne shipmentId
 */
export async function createShipmentFromPOs({ poIds, lines, meta }) {
  if (!poIds?.length) throw new Error("poIds manquants");

  const clean = (lines || [])
    .map((l) => {
      const qty = Math.max(0, Number(l.qtyPlanned || l.remain || 0));
      return {
        poId: l.poId,
        itemId: l.itemId,
        name: l.name || null,
        qty,                 // 👈 quantité EXPÉDIÉE
        rcvdQty: 0,          // 👈 rien reçu au départ
        unitCost: Number(l.unitCost || 0),
      };
    })
    .filter((l) => l.itemId && l.poId && l.qty > 0);

  if (!clean.length) throw new Error("Aucune ligne valide à expédier.");

  const ref = await addDoc(collection(db, "shipments"), {
    poIds,
    status: "shipped",        // ou "draft" si tu veux une étape brouillon
    lines: clean,
    meta: meta || null,       // {carrier, container, departAt, trackingNo, note, ...}
    createdAt: serverTimestamp(),
    shippedAt: serverTimestamp(), // cohérent avec status "shipped"
  });

   // 2) verrouiller tous les PO concernés
  await Promise.all(
    poIds.map(id =>
      updateDoc(doc(db, "purchaseOrders", id), {
        locked: true,
        updatedAt: serverTimestamp(),
      })
    )
  );

  return { shipmentId: ref.id };
}